import React, { useState, useEffect, useRef, useCallback } from "react";
import { Pressable, Text, TextInput, View, ActivityIndicator } from "react-native";
import { StyleProp, TextStyle, ViewStyle } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { RelatedIssueItem } from "../../../store/slices/ticketingPortalSlice";
import styles from "../SubmitTicketScreenStyles";
import mobileStyles from "../SubmitTicketScreen.mobile.style";
import { formatDistanceToNow } from "date-fns";
import { useTheme } from "../../../theme";

type Props = {
  query: string;
  loading: boolean;
  issues: RelatedIssueItem[];
  attachedIssueIds: string[];
  onQueryChange: (value: string) => void;
  onAttachIssue: (issueId: string) => void;
  onViewTicket?: (ticketNumber: string) => void;
  onAddAffectedLocation?: (ticketNumber: string, title: string, backendTicketId?: number) => void;
  mobile?: boolean;
  searchResults?: any[];
  onSearch?: (query: string) => void;
  isSearching?: boolean;
  userLocation?: string;
  userEmail?: string;
  isDark?: boolean;
  colors?: any;
};

const RelatedIssuesPanel: React.FC<Props> = ({
  query,
  loading,
  issues,
  attachedIssueIds,
  onQueryChange,
  onAttachIssue,
  onViewTicket,
  onAddAffectedLocation,
  mobile,
  searchResults = [],
  onSearch,
  isSearching = false,
  userLocation,
  userEmail,
  isDark: isDarkProp,
  colors: colorsProp,
}) => {
  // Call sites don't pass isDark/colors, so fall back to the active theme.
  const theme = useTheme();
  const isDark = isDarkProp ?? theme.resolvedTheme === "dark";
  const colors = colorsProp ?? theme.colors;
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [localQuery, setLocalQuery] = useState(query);
  const [reviewedCheckbox, setReviewedCheckbox] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchRef = useRef(onSearch);
  const s = mobile ? mobileStyles : styles;

  // Update ref when onSearch changes
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Debounced search effect
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmedQuery = localQuery.trim();
    if (trimmedQuery.length < 3) {
      onSearchRef.current?.(trimmedQuery);
      return;
    }

    if (trimmedQuery.length >= 3 && onSearchRef.current) {
      debounceTimerRef.current = setTimeout(() => {
        onSearchRef.current?.(trimmedQuery);
      }, 500);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [localQuery]); // Only depend on localQuery, not onSearch

  // Sync with parent query prop
  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  const handleQueryChange = (value: string) => {
    setLocalQuery(value);
    onQueryChange(value);
    if (value.trim().length < 3) {
      setReviewedCheckbox(false);
      // Clear search results when query is too short
      if (searchResults && searchResults.length > 0 && onSearchRef.current) {
        // Don't trigger a new search, just clear local state
      }
    }
  };

  const handleKeyPress = (e: any) => {
    if (e.nativeEvent.key === 'Enter') {
      const trimmedQuery = localQuery.trim();
      if (trimmedQuery.length >= 3 && onSearchRef.current) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        onSearchRef.current(trimmedQuery);
      }
    }
  };

  const formatRelativeTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  const trimmedQuery = localQuery.trim();
  const shouldShowResults = trimmedQuery.length >= 3;
  const displayResults = shouldShowResults ? searchResults : [];
  const hasResults = displayResults.length > 0;
  const showReviewCheckbox = hasResults && shouldShowResults;
  const getStatusStyle = (
    status: string
  ): {
    badgeStyle: StyleProp<ViewStyle>;
    textStyle: StyleProp<TextStyle>;
    dotStyle: StyleProp<ViewStyle>;
  } => {
    switch (status) {
      case "In Progress":
        return {
          badgeStyle: styles.statusProgress,
          textStyle: styles.statusProgressText,
          dotStyle: styles.statusProgressDot,
        };
      case "Escalated to IT":
        return {
          badgeStyle: styles.statusWaitingIt,
          textStyle: styles.statusWaitingItText,
          dotStyle: styles.statusWaitingItDot,
        };
      case "Waiting on User":
        return {
          badgeStyle: styles.statusWaitingUser,
          textStyle: styles.statusWaitingUserText,
          dotStyle: styles.statusWaitingUserDot,
        };
      default:
        return {
          badgeStyle: styles.statusDefault,
          textStyle: styles.statusDefaultText,
          dotStyle: styles.statusDefaultDot,
        };
    }
  };

  const dk = isDark && colors;
  return (
    <View
      style={[
        s.existingCard,
        // The light style paints a CSS `background` gradient (not swept), which would cover backgroundColor.
        dk && ({ backgroundColor: colors.background.surface, borderColor: colors.border.default, background: colors.background.surface } as any),
      ]}
    >
      <View style={s.existingCardHeader}>
        <View style={[s.existingIconWrap, dk && { backgroundColor: "#1a3a55" }]}>
          <MaterialIcons name="search" size={18} color={dk ? colors.brand.primary : "#005cbc"} />
        </View>
        <Text style={[s.panelTitle, dk && { color: colors.text.primary }]}>Check for Existing Issues</Text>
      </View>
      <Text style={[s.panelSubtitle, dk && { color: colors.text.secondary }]}>
        Someone else may already be reporting the same problem. Attaching your location gives the Sustain Team the full impact picture and avoids duplicates.
      </Text>

      <View style={s.searchWrap}>
        <MaterialIcons name="search" size={18} color={dk ? colors.text.disabled : "#818181"} style={s.searchIcon} />
        <TextInput
          style={[s.searchInput, dk && { backgroundColor: colors.background.elevated, borderColor: colors.border.default, color: colors.text.primary }]}
          value={localQuery}
          onChangeText={handleQueryChange}
          onKeyPress={handleKeyPress}
          placeholder="Search by keywords or ticket ID"
          placeholderTextColor={dk ? colors.text.disabled : "#9099aa"}
        />
        {isSearching && <ActivityIndicator size="small" color={dk ? colors.brand.primary : "#034895"} style={{ marginLeft: 8 }} />}
      </View>

      {localQuery.trim().length > 0 && localQuery.trim().length < 3 && (
        <Text style={[styles.helperText, { color: dk ? colors.text.secondary : "#6B7280", marginTop: 8 }]}>
          Enter at least 3 characters to search
        </Text>
      )}

      {(shouldShowResults && (loading || isSearching)) ? (
        <Text style={[styles.helperText, dk && { color: colors.text.secondary }]}>Searching related issues...</Text>
      ) : null}

      {displayResults.map((issue) => {
        // Prefer affected-location precheck from backend for submit-page attach state.
        const backendAlreadyAttached = (issue as any).alreadyAttached === true;
        const isOldRelatedIssue = attachedIssueIds.includes(issue.id);
        const attached = backendAlreadyAttached || isOldRelatedIssue;
        
        // Debug log to verify matching
        if (issue.id) {
          const issueData = issue as any;
          console.log('[RelatedIssuesPanel DEBUG]', {
            ticketNumber: issueData.ticketNumber || issueData.serviceRef,
            ticketId: issue.id,
            backendAlreadyAttached,
            alreadyAttachedValue: issueData.alreadyAttached,
            alreadyAttachedType: typeof issueData.alreadyAttached,
            isOldRelatedIssue,
            finalAttached: attached,
            issueKeys: Object.keys(issueData),
            issueData: issueData
          });
        }
        const hovered = hoveredId === issue.id;
        const { badgeStyle, textStyle, dotStyle } = getStatusStyle(issue.status);
        const ticketId = issue.ticketNumber || issue.id;
        const backendTicketId = Number.parseInt(String(issue.id), 10);
        const isValidBackendId = !Number.isNaN(backendTicketId);
        const description = issue.issueDescription || "";
        const createdAt = issue.createdAt ? formatRelativeTime(issue.createdAt) : "";
        
        return (
          <View
            key={issue.id}
            // @ts-ignore — onMouseEnter/onMouseLeave are valid on React Native Web
            onMouseEnter={() => setHoveredId(issue.id)}
            onMouseLeave={() => setHoveredId(null)}
            style={[
              s.existingTicket,
              !mobile && hovered ? styles.existingTicketHovered : null,
              dk && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
            ]}
          >
            <View style={[s.existingTicketBody, { flex: 1 }]}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <Text style={[s.ticketId, { fontWeight: '600', color: dk ? colors.brand.primary : '#034895' }]}>{ticketId}</Text>
                <View style={[styles.statusBadge, badgeStyle]}>
                  <View style={[styles.statusDot, dotStyle]} />
                  <Text style={[styles.statusBadgeText, textStyle]}>{issue.status}</Text>
                </View>
              </View>
              
              <Text style={[s.ticketTitle, { fontWeight: '700', marginBottom: 6 }, dk && { color: colors.text.primary }]}>{issue.title}</Text>
              
              {description && (
                <Text style={[styles.helperText, { marginBottom: 8, lineHeight: 18 }, dk && { color: colors.text.secondary }]} numberOfLines={2}>
                  {description.length > 100 ? `${description.substring(0, 100)}...` : description}
                </Text>
              )}
              
              <View style={s.existingMetaRow}>
                {createdAt && (
                  <Text style={[s.ticketMeta, { color: dk ? colors.text.disabled : '#9CA3AF' }]}>
                    {createdAt}
                  </Text>
                )}
                <Text style={[s.ticketMeta, dk && { color: colors.text.secondary }]}>
                  {issue.locationsLabel || `${issue.affectedLocations ?? issue.stakeholderEmails?.length ?? 0} locations affected`}
                </Text>
                <Text style={[s.ticketMeta, dk && { color: colors.text.secondary }]}>{issue.serviceRef || `${issue.urgency || 'Medium'} urgency`}</Text>
              </View>
              
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {onViewTicket && (
                  <Pressable
                    onPress={() => onViewTicket(ticketId)}
                    style={({ pressed }) => [
                      {
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 4,
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 6,
                        borderWidth: 1,
                        borderColor: dk ? colors.brand.primary : '#034895',
                        backgroundColor: dk ? 'transparent' : '#FFFFFF',
                      },
                      pressed && { opacity: 0.7 }
                    ]}
                  >
                    <MaterialIcons name="open-in-new" size={14} color={dk ? colors.brand.primary : "#034895"} />
                    <Text style={{ fontSize: 13, fontWeight: '600', color: dk ? colors.brand.primary : '#034895' }}>View Details</Text>
                  </Pressable>
                )}
                
                {onAddAffectedLocation && !attached && (
                  <Pressable
                    onPress={() => {
                      if (ticketId) {
                        onAddAffectedLocation(ticketId, issue.title, isValidBackendId ? backendTicketId : undefined);
                      }
                    }}
                    style={({ pressed }) => [
                      {
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 4,
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 6,
                        backgroundColor: dk ? colors.brand.primary : '#034895',
                        borderWidth: 1,
                        borderColor: dk ? colors.brand.primary : '#034895',
                      },
                      pressed && { opacity: 0.7 }
                    ]}
                  >
                    <MaterialIcons name="person-add" size={14} color="#FFFFFF" />
                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#FFFFFF' }}>I'm Affected Too</Text>
                  </Pressable>
                )}
                
                {attached && (
                  <View style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 6,
                    backgroundColor: dk ? '#3b1a1a' : '#FDEDED',
                    borderWidth: 1,
                    borderColor: dk ? '#7f1d1d' : '#F5C2C7',
                    opacity: 0.85,
                  }}>
                    <MaterialIcons name="block" size={14} color={dk ? "#f87171" : "#B91C1C"} />
                    <Text style={{ fontSize: 13, fontWeight: '600', color: dk ? "#f87171" : '#B91C1C' }}>Your Location Already Attached</Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        );
      })}

      {showReviewCheckbox && (
        <View style={{ marginTop: 16, padding: 12, backgroundColor: dk ? '#2a1e00' : '#FFF4E5', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#FF8C09' }}>
          <Pressable
            onPress={() => setReviewedCheckbox(!reviewedCheckbox)}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
          >
            <MaterialIcons
              name={reviewedCheckbox ? "check-box" : "check-box-outline-blank"}
              size={20}
              color={reviewedCheckbox ? (dk ? colors.brand.primary : "#034895") : (dk ? colors.text.disabled : "#9CA3AF")}
            />
            <Text style={{ fontSize: 13, color: dk ? colors.text.primary : '#1F2937', flex: 1 }}>
              I've reviewed these tickets and still want to create a new one
            </Text>
          </Pressable>
          {reviewedCheckbox && (
            <Text style={{ fontSize: 12, color: dk ? colors.text.secondary : '#6B7280', marginTop: 8, marginLeft: 28 }}>
              You can now continue with creating your ticket below
            </Text>
          )}
        </View>
      )}

      {!loading && !isSearching && displayResults.length === 0 && shouldShowResults ? (
        <Text style={[styles.helperText, dk && { color: colors.text.secondary }]}>No similar issues found. Continue with creating a new ticket.</Text>
      ) : null}
    </View>
  );
};

export default RelatedIssuesPanel;
